/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import DefaultApiLimit from '../../config/defaultLimit';
import { filterType } from '../../config/enum';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  APIResponseInterface,
  PaymentInitialProperties,
} from '../../interface/payment-management.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import FormatUtcString from '../../shared-functions/FormatUtcString';
import cpoTransactionApi from '../../utils/api/cpo-transaction';

import { setNotificationMessage } from './NotificationManagementSlice';

interface FetchParameters {
  search?: string;
  page?: number;
  limit?: number;
  startDate?: string;
  endDate?: string;
  sortBy?: string;
  sortField?: string;
  accountCustomIds?: string[];
  accountCustomId?: string;
  [key: string]: any;
}

/**
 * Async thunk to fetch CDR transactions for an account payout view.
 * @param {FetchParameters} data - The query parameters.
 * @param {any} thunkAPI - The thunk API object containing dispatch.
 * @returns {Promise<APIResponseInterface>} Resolves with API response interface.
 */
export const fetchAccountPayoutTransactions = createAsyncThunk<
  APIResponseInterface,
  FetchParameters
>(
  'accountPayoutTransaction/fetchAccountPayoutTransactions',
  async (data: FetchParameters, { dispatch }) => {
    try {
      const queryData: Record<string, any> = { ...data };
      const accountCustomIdValue = data.accountCustomId || data.accountCustomIds;
      delete queryData.accountCustomId;
      delete queryData.accountCustomIds;

      if (accountCustomIdValue) {
        queryData.accountCustomId = Array.isArray(accountCustomIdValue)
          ? accountCustomIdValue
          : [accountCustomIdValue];
      }

      const response = await cpoTransactionApi.fetchCpoTransactions(removeEmptyKeys(queryData));
      return {
        ...response.data,
        results: (response.data?.results || []).map((roleData: any) => ({
          ...roleData,
          date: ConvertUTCtoDate(roleData.createdAt),
          time: FormatUtcString(ConvertUTCtoHHMM(roleData.createdAt)),
          id: roleData._id || roleData.id,
        })),
      };
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message || 'Something went wrong!',
          code: error?.response?.data?.code || 200,
          status: true,
        }),
      );
      throw error;
    }
  },
);

/**
 * Async thunk to refresh CDR transactions for an account payout view.
 * @param {FetchParameters} data - The query parameters.
 * @returns {Promise<APIResponseInterface>} Resolves with API response interface.
 */
export const refreshAccountPayoutTransactions = createAsyncThunk<
  APIResponseInterface,
  FetchParameters
>('accountPayoutTransaction/refreshAccountPayoutTransactions', async (data: FetchParameters) => {
  const queryData: Record<string, any> = { ...data };
  const accountCustomIdValue = data.accountCustomId || data.accountCustomIds;
  delete queryData.accountCustomId;
  delete queryData.accountCustomIds;

  if (accountCustomIdValue) {
    queryData.accountCustomId = Array.isArray(accountCustomIdValue)
      ? accountCustomIdValue
      : [accountCustomIdValue];
  }

  const response = await cpoTransactionApi.fetchCpoTransactions(removeEmptyKeys(queryData));
  return {
    ...response.data,
    results: (response.data?.results || []).map((roleData: any) => ({
      ...roleData,
      date: ConvertUTCtoDate(roleData.createdAt),
      time: FormatUtcString(ConvertUTCtoHHMM(roleData.createdAt)),
      id: roleData._id || roleData.id,
    })),
  };
});

/**
 * Async thunk to fetch filters for CDR transactions.
 * @param {any} _payload - Optional payload.
 * @param {any} thunkAPI - The thunk API object containing dispatch.
 * @returns {Promise<CommonFilterProperties[]>} Resolves with array of filter properties.
 */
export const fetchAccountPayoutTransactionFilters = createAsyncThunk<
  CommonFilterProperties[],
  void | { accountCustomId?: string[] }
>(
  'accountPayoutTransaction/fetchAccountPayoutTransactionFilters',
  async (_payload, { dispatch }) => {
    try {
      const response = await cpoTransactionApi.fetchCpoTransactionFilters();
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(
        response.data,
      ).filter((filter) => filter.type !== filterType.DATE_RANGE);
      return updatedResponse;
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message || 'Something went wrong!',
          code: error?.response?.data?.code || 200,
          status: true,
        }),
      );
      throw error;
    }
  },
);

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

const initialState: PaymentInitialProperties = {
  payments: [],
  limit: DefaultApiLimit.limit,
  page: DefaultApiLimit.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: null,
  sortField: null,
};

export const AccountPayoutTransactionSlice = createSlice({
  name: 'accountPayoutTransaction',
  initialState,
  reducers: {
    /**
     * Update filters in state.
     * @param {PaymentInitialProperties} state - The current state.
     * @param {PayloadAction<FilterUpdateProperties>} action - The action containing filter update.
     */
    updateAccountPayoutTransactionFilters: (
      state,
      action: PayloadAction<FilterUpdateProperties>,
    ) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
        state.sortBy = null;
        state.sortField = null;
      }
    },

    /**
     * Disables the filter updated flag.
     * @param {PaymentInitialProperties} state - The current state.
     */
    disableAccountPayoutTransactionFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },

    /**
     * Sets sort data in state.
     * @param {PaymentInitialProperties} state - The current state.
     * @param {PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>} action - Sort data payload.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },

    /**
     * Resets state to initial state.
     * @param {PaymentInitialProperties} state - The current state.
     */
    resetAccountPayoutTransactionData: (state) => {
      state.payments = [];
      state.page = DefaultApiLimit.page;
      state.limit = DefaultApiLimit.limit;
      state.totalPages = 0;
      state.totalResults = 0;
      state.loading = false;
      state.filters = availableFilters;
      state.isFilterUpdated = false;
      state.sortBy = null;
      state.sortField = null;
    },
  },
  /**
   * Extra reducers for async thunk actions.
   * @param {ActionReducerMapBuilder<PaymentInitialProperties>} builder - Redux builder.
   */
  extraReducers: (builder: ActionReducerMapBuilder<PaymentInitialProperties>) => {
    builder
      .addCase(fetchAccountPayoutTransactions.pending, (state: PaymentInitialProperties) => {
        state.loading = true;
      })
      .addCase(
        fetchAccountPayoutTransactions.fulfilled,
        (state: PaymentInitialProperties, action: PayloadAction<APIResponseInterface>) => {
          state.loading = false;
          state.payments = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(fetchAccountPayoutTransactions.rejected, (state: PaymentInitialProperties) => {
        state.loading = false;
      });
    builder.addCase(
      refreshAccountPayoutTransactions.fulfilled,
      (state: PaymentInitialProperties, action: PayloadAction<APIResponseInterface>) => {
        state.payments = [...action.payload.results];
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
      },
    );
    builder
      .addCase(fetchAccountPayoutTransactionFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchAccountPayoutTransactionFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...availableFilters, ...action.payload].filter(
            (object, index, self) =>
              index === self.findIndex((t) => t.key === object.key) &&
              object.type !== filterType.DATE_RANGE,
          );
        },
      )
      .addCase(fetchAccountPayoutTransactionFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

export const {
  updateAccountPayoutTransactionFilters,
  disableAccountPayoutTransactionFilterApplied,
  setSortData,
  resetAccountPayoutTransactionData,
} = AccountPayoutTransactionSlice.actions;

export default AccountPayoutTransactionSlice.reducer;
